import type { MetadataFieldDef, MetadataFieldType, OptionUIType } from '@tkpod/shared';

// ---------------------------------------------------------------------------
// Default metadata schemas per UI type
// Only fields that designer/portal actively reads should be here.
// Everything else is optional -- admin adds via "Add Field".
// ---------------------------------------------------------------------------

// `hexCode` is intentionally NOT marked required. The admin UI auto-fills it
// from the color name via the shared color-name-to-hex map when the user
// leaves it blank, and the API does the same on save as a server-side
// fallback. Marking it required would block save on names that already have
// a known hex (e.g. "Heliconia", "Forest Green").
const COLOR_SWATCH_SCHEMA: MetadataFieldDef[] = [
  { key: 'hexCode', label: 'Hex Code', type: 'color', showInTable: true },
  { key: 'hexCode2', label: 'Hex Code 2', type: 'color', showInTable: true },
];

const IMAGE_CARD_SCHEMA: MetadataFieldDef[] = [
  { key: 'imageUrl', label: 'Image', type: 'image', showInTable: true },
];

const DEFAULT_SCHEMAS: Partial<Record<OptionUIType, MetadataFieldDef[]>> = {
  COLOR_SWATCH: COLOR_SWATCH_SCHEMA,
  IMAGE_CARD: IMAGE_CARD_SCHEMA,
};

// ---------------------------------------------------------------------------
// Field-type compatibility per UI type
// ---------------------------------------------------------------------------

const COMPATIBLE_FIELD_TYPES: Record<OptionUIType, MetadataFieldType[]> = {
  COLOR_SWATCH: ['color', 'string', 'number', 'select', 'boolean', 'image'],
  BUTTON_GROUP: ['string', 'number', 'select', 'boolean'],
  DROPDOWN: ['string', 'number', 'select', 'boolean'],
  RADIO: ['string', 'number', 'select', 'boolean'],
  IMAGE_CARD: ['image', 'string', 'number', 'select', 'boolean'],
};

export function getCompatibleFieldTypes(uiType: OptionUIType): MetadataFieldType[] {
  return COMPATIBLE_FIELD_TYPES[uiType] ?? ['string', 'number', 'select', 'boolean'];
}

// ---------------------------------------------------------------------------
// Default field keys (locked -- cannot be deleted by admin)
// ---------------------------------------------------------------------------

const DEFAULT_FIELD_KEYS: Partial<Record<OptionUIType, string[]>> = {
  COLOR_SWATCH: COLOR_SWATCH_SCHEMA.map((f) => f.key),
  IMAGE_CARD: IMAGE_CARD_SCHEMA.map((f) => f.key),
};

export function getDefaultFieldKeys(uiType: OptionUIType): string[] {
  return DEFAULT_FIELD_KEYS[uiType] ?? [];
}

// ---------------------------------------------------------------------------
// Effective schema resolution
// ---------------------------------------------------------------------------

function isValidSchema(data: unknown): data is MetadataFieldDef[] {
  if (!Array.isArray(data) || data.length === 0) return false;
  const first = data[0];
  return first != null && typeof first === 'object' && 'key' in first && 'type' in first;
}

export function getEffectiveSchema(
  explicitSchema: MetadataFieldDef[] | unknown[] | undefined | null,
  uiType: OptionUIType,
): MetadataFieldDef[] {
  if (isValidSchema(explicitSchema)) {
    return explicitSchema;
  }
  return DEFAULT_SCHEMAS[uiType] ?? [];
}

export function getDefaultSchemaForUiType(uiType: OptionUIType): MetadataFieldDef[] {
  return DEFAULT_SCHEMAS[uiType] ?? [];
}
